<script setup lang="ts">
import { ref } from 'vue';
import { toast } from '@/helpers/toast';
import { isLoanAppComplete } from '@/helpers/typeGuards';
import { useApplicationApi } from '@/helpers/useApplicationApi';
import { useCustomerQuoteProcess } from '@/helpers/useCustomerQuoteProcess';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid';
import { useForm } from 'vee-validate';
import type { Applicant } from '@/types';
import { updateToken } from '@/api/backend/_http';
import { yupObject } from '@/validation';
import { AppValues } from '@/helpers/const';
import type { ApplicationType } from '@/api/enums';

interface ReferralEntry {
  isRequired: boolean;
}

const { loanApp, state, send, saveAndGoNext, goBack } = useCustomerQuoteProcess();

const { handleSubmit } = useForm<ReferralEntry>({
  validationSchema: yupObject({}),
  initialValues: { isRequired: loanApp.value.userType === 'Primary' },
  validateOnMount: false,
});

const saveAndContinue = handleSubmit(() => {
  const primaryApplicant = loanApp.value.applicants.find((a) => a.type === 'primary');
  let applicants: Applicant[];
  if (primaryApplicant) {
    applicants = [primaryApplicant, ...loanApp.value.applicants.filter((a) => a.type !== 'primary')];
  } else {
    applicants = loanApp.value.applicants;
  }
  send({
    type: 'UPDATE',
    loanApp: {
      ...loanApp.value,
      applicants,
      utmMetrics: {
        ...loanApp.value.utmMetrics,
      },
    },
  });
  submitApplicationQuote();
});

const showModal = ref(false);
const modalType = ref<'canCancel' | 'cannotProceed'>('canCancel');
const appApi = useApplicationApi();
const isLoading = appApi.isLoading;

const submitApplicationQuote = async () => {
  if (!loanApp.value.userType) {
    loanApp.value.userType = 'Primary';
  }

  if (isLoanAppComplete(loanApp.value)) {
    const result = await appApi.submitScenario({
      loanApp: {
        ...loanApp.value,
        applicationType: 'scenario' as ApplicationType,
      },
      sectionStates: state.value.context.sectionStates,
    });

    if (result.success) {
      toast({ type: 'success', title: 'Quote submitted successfully' });
      updateToken(null);
      saveAndGoNext();
    } else {
      toast({
        type: 'danger',
        title: 'Error',
        description: `A system error has occurred. Please try again otherwise contact ${AppValues.COMPANY_NAME} on ${AppValues.CONTACT} for support`,
      });
    }
  } else {
    console.error('loan incomplete!', loanApp.value);
    toast({
      type: 'danger',
      title: 'Error',
      description: `Could not submit your quote. It looks like something is missing in the quote. Please contact ${AppValues.COMPANY_NAME} on ${AppValues.CONTACT} for support.`,
    });
  }
};

const onConfirm = async () => {
  showModal.value = false;
  await submitApplicationQuote();
};
</script>

<template>
  <div class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        <BxHighlight> Confirmation </BxHighlight>
      </h2>
    </div>
    <div>
      <div>
        <h2 class="mr-1 inline text-lg font-medium">
          Please provide any additional information regarding
          {{ loanApp.userType === 'Primary' ? 'your' : 'the' }} loan scenario
        </h2>
        <span class="text-xs">(optional)</span>
      </div>
      <div>
        <p>
          Sharing additional information can help speed up the quote process for you, and it gives our team a better
          picture of your unique situation. If there's anything you'd like to add, particularly about your asset
          position, feel free to share in the space below.
        </p>
      </div>
      <BxTextarea id="submission-reason-for-fund" v-model="loanApp.summaryNotes" class="mt-2 w-full" :rows="5" />
    </div>
    <div class="mt-12">
      <BxButton id="submission-submit" variant="primary" :loading="isLoading" @click="saveAndContinue()"
        >Submit</BxButton
      >
      <BxButton variant="link" class="ml-4" :disabled="isLoading" @click="goBack()">Back</BxButton>
    </div>
    <BxModal
      :open="showModal && modalType === 'canCancel'"
      :icon="ExclamationTriangleIcon"
      title="Warning"
      confirm-label="Yes"
      cancel-label="No"
      @close="showModal = false"
      @confirm="onConfirm()"
    >
      <h4>You have an existing active quote, are you sure you want to proceed and cancel the previous quote?</h4>
    </BxModal>
    <BxModal
      :open="showModal && modalType === 'cannotProceed'"
      :icon="ExclamationTriangleIcon"
      title="Warning"
      confirm-label="Yes"
      cancel-label="No"
      @close="showModal = false"
      @confirm="onConfirm()"
    >
      <h4>
        Our records indicate that there is currently an active quote with these contact details. Please call us on
        {{ AppValues.CONTACT }} to discuss changing your quote.
      </h4>
      <template #buttons>
        <BxButton variant="primary" @click="showModal = false">Close</BxButton>
      </template>
    </BxModal>
  </div>
</template>
